import { DevsuWhiteIcon } from '@/assets';
import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  ConfirmationBannerSliceDefault,
  ConfirmationBannerSliceWithContainer,
} from '../../../prismicio-types';
import { PrismicNextImage } from '@prismicio/next';

enum SLICE_VARIATION {
  default = 'default',
  withContainer = 'withContainer',
}

/**
 * Props for `ConfirmationBanner`.
 */
export type ConfirmationBannerProps =
  SliceComponentProps<Content.ConfirmationBannerSlice>;

/**
 * Component for "ConfirmationBanner" Slices.
 */
const ConfirmationBanner = ({
  slice,
}: ConfirmationBannerProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative"
    >
      {slice.variation === SLICE_VARIATION.default && defaultVariation(slice)}
      {slice.variation === SLICE_VARIATION.withContainer &&
        withContainerVariation(slice)}
    </section>
  );
};

const defaultVariation = (
  slice: ConfirmationBannerSliceDefault,
): JSX.Element => {
  const { title, content } = slice.primary;
  return (
    <div
      className="min-h-screen flex flex-col justify-center items-center text-white text-center gap-y-8 px-6 py-20"
      style={{
        background:
          'radial-gradient(ellipse at center, #0f423a 0%, #0e1110 70%)',
      }}
    >
      {/* Star pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.15) 0 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Success checkmark */}
      <div className="relative z-10 w-20 h-20 rounded-full bg-gradient-to-b from-[#05F273] to-[#03BF9E] flex items-center justify-center shadow-[0_0_30px_rgba(5,242,115,0.4)]">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 12L10 17L19 8"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Title */}
      <div className="relative z-10 text-3xl md:text-5xl font-extrabold max-w-3xl break-words">
        <PrismicRichText
          field={title}
          components={{
            heading1: ({ children }) => (
              <h1 className="text-white">{children}</h1>
            ),
            heading2: ({ children }) => (
              <h2 className="text-white">{children}</h2>
            ),
            strong: ({ children }) => (
              <strong className="text-[#05F273]">{children}</strong>
            ),
          }}
        />
      </div>

      <DevsuWhiteIcon />

      {/* Content */}
      <div className="relative z-10 text-xl font-medium max-w-2xl text-white/90">
        <PrismicRichText
          field={content}
          components={{
            hyperlink: ({ children, node }) => (
              <a
                href={node.data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#05F273] underline hover:text-[#03d466] transition-colors"
              >
                {children}
              </a>
            ),
          }}
        />
      </div>
    </div>
  );
};

const withContainerVariation = (
  slice: ConfirmationBannerSliceWithContainer,
): JSX.Element => {
  const { header_image: headerImage, icon, title, content } = slice.primary;
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-20"
      style={{
        background:
          'radial-gradient(ellipse at center, #0f423a 0%, #0e1110 70%)',
      }}
    >
      {/* Star pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.15) 0 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative z-10 p-10 md:p-16 rounded-[20px] backdrop-blur-[47.5px] shadow-lg bg-[rgba(31,34,40,0.8)] my-12 flex flex-col gap-y-10 items-center text-center max-w-4xl">
        {headerImage?.url && (
          <PrismicNextImage
            field={headerImage}
            className="w-full max-w-[23.75rem]"
          />
        )}

        {icon?.url ? (
          <PrismicNextImage field={icon} />
        ) : (
          /* Default success checkmark */
          <div className="w-20 h-20 rounded-full bg-gradient-to-b from-[#05F273] to-[#03BF9E] flex items-center justify-center shadow-[0_0_30px_rgba(5,242,115,0.4)]">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 12L10 17L19 8"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}

        {/* Title */}
        <div className="max-w-[45rem] text-3xl md:text-4xl font-bold text-white">
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => <h1>{children}</h1>,
              heading2: ({ children }) => <h2>{children}</h2>,
              strong: ({ children }) => (
                <strong className="text-[#05F273]">{children}</strong>
              ),
            }}
          />
        </div>

        {/* Content */}
        <div className="max-w-[51rem] text-lg text-white/90">
          <PrismicRichText
            field={content}
            components={{
              hyperlink: ({ children, node }) => (
                <a
                  href={node.data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#05F273] underline hover:text-[#03d466] transition-colors"
                >
                  {children}
                </a>
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ConfirmationBanner;
